import { useEffect, useRef } from "react";

const GREENS = ["#2E6930", "#3E7C33", "#356F2E", "#4C8A3F", "#57A04A"];

export const PixelGrass = () => {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    const parent = canvas.parentElement;
    const ctx = canvas.getContext("2d");
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let w = 0, h = 0, blades = [], risers = [], raf;
    let amp = 5, ampTarget = 5;
    const mouse = { x: -9999, y: -9999, active: false };

    const rand = (seed) => {
      const x = Math.sin(seed * 12.9898) * 43758.5453;
      return x - Math.floor(x);
    };

    const build = () => {
      const rect = parent.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = w + "px";
      canvas.style.height = h + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      blades = [];
      const step = w < 768 ? 7 : 5;
      for (let x = 0; x < w; x += step) {
        const r = rand(x);
        blades.push({
          x,
          hgt: h * (0.22 + r * 0.55),
          ph: r * Math.PI * 2,
          col: GREENS[(r * GREENS.length) | 0],
          lean: (r - 0.5) * 2,
        });
      }
      risers = Array.from({ length: w < 768 ? 12 : 30 }, () => ({
        x: Math.random() * w, y: h, vy: 0.3 + Math.random() * 0.6, life: Math.random(),
      }));
    };

    const draw = (now) => {
      ctx.clearRect(0, 0, w, h);
      const t = now;
      amp += (ampTarget - amp) * 0.05;

      for (const b of blades) {
        let ripple = 0;
        if (mouse.active) {
          const dist = Math.abs(b.x - mouse.x);
          if (dist < 160) {
            const k = 1 - dist / 160;
            ripple = Math.sin(t * 0.006 - dist * 0.045) * 20 * k;
          }
        }
        const wave = Math.sin(b.x * 0.035 + t * 0.0013 + b.ph) * amp + ripple;
        const n = Math.max(3, Math.floor(b.hgt / 6));
        for (let j = 0; j < n; j++) {
          const frac = j / n;
          const y = h - 4 - j * 6 + wave * frac;
          ctx.globalAlpha = 0.3 + 0.6 * frac;
          ctx.fillStyle = b.col;
          ctx.fillRect(b.x + wave * frac * 0.7 + b.lean * j * 0.4, y, 3, 3);
        }
      }

      ctx.fillStyle = "#57A04A";
      for (const r of risers) {
        r.y -= r.vy;
        r.life -= 0.0028;
        if (r.y < h * 0.08 || r.life <= 0) {
          r.x = Math.random() * w;
          r.y = h - 4;
          r.life = 1;
          r.vy = 0.3 + Math.random() * 0.6;
        }
        ctx.globalAlpha = 0.5 * r.life;
        ctx.fillRect(r.x + Math.sin(t * 0.001 + r.x) * 4, r.y, 2.4, 2.4);
      }
      ctx.globalAlpha = 1;
      if (!reduced) raf = requestAnimationFrame(draw);
    };

    const onMove = (e) => {
      const r = parent.getBoundingClientRect();
      mouse.x = e.clientX - r.left;
      mouse.y = e.clientY - r.top;
      mouse.active = true;
      ampTarget = 11;
    };
    const onLeave = () => { mouse.active = false; ampTarget = 5; };

    parent.addEventListener("mousemove", onMove);
    parent.addEventListener("mouseleave", onLeave);
    window.addEventListener("resize", build);
    build();
    raf = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(raf);
      parent.removeEventListener("mousemove", onMove);
      parent.removeEventListener("mouseleave", onLeave);
      window.removeEventListener("resize", build);
    };
  }, []);

  return (
    <section data-testid="pixel-grass-interlude" className="relative h-[180px] sm:h-[230px] lg:h-[260px] overflow-hidden" aria-hidden="true">
      <canvas ref={ref} className="absolute inset-0" />
    </section>
  );
};
